import { useEffect, useState } from "react";
import Router from "next/router";
import config from "@/libs/config";
import { useIsLogedInFromBizsol } from "@/libs/loginSource";
import useFixedAuth from "@/libs/useFixedAuth";
import { AuthStatus } from "@/contexts/AuthContext";
import { useGetMyBizsolStateLazyQuery, BizsolState } from "@/generated/graphql";
import { moveToErrorPage } from "@/libs/errors";
import useRollbarUtil from "../hooks/useRollbarUtil";

enum BizsolConnectedState {
  Connected,
  Waiting,
  NotConnected,
}

export const BizsolRedirectTopFromBfmPage = () => {
  const { rollbar } = useRollbarUtil();
  const fqdn = config.BIZSOL_ANSER_FQDN;
  const env = config.STAGE == "prod" ? "c" : "d";

  // Bizsol連携済みかどうか取得する(async)
  const [bizsolConnectedState, setBizsolConnectedState] =
    useState<BizsolConnectedState>(null);
  const { authStatus } = useFixedAuth();
  const [getBizsolStateQuery, { called }] = useGetMyBizsolStateLazyQuery({
    onCompleted: (data) => {
      if (
        data?.me?.bizsolState === BizsolState.Admin ||
        data?.me?.bizsolState === BizsolState.Master ||
        data?.me?.bizsolState === BizsolState.Member
      ) {
        setBizsolConnectedState(BizsolConnectedState.Connected);
      } else if (data?.me?.bizsolState === BizsolState.Waiting) {
        setBizsolConnectedState(BizsolConnectedState.Waiting);
      } else {
        setBizsolConnectedState(BizsolConnectedState.NotConnected);
      }
    },
    onError: (err) => {
      rollbar.error("Failed to get Bizsol state", err);
      moveToErrorPage();
    },
  });
  useEffect(() => {
    if (authStatus !== AuthStatus.SignedIn) {
      return;
    }
    if (!called) {
      getBizsolStateQuery();
    }
  }, [authStatus]);

  // サインイン元がBizsolかどうか取得する(sync)
  const isLogedInFromBizsol = useIsLogedInFromBizsol();

  // Bizsol連携状態・どこからサインインしたかによって、リダイレクト先を変える
  // 1. Biz連携有り＆Bizsolからサインインした場合
  //    → BizsolのSSOによる相互遷移用トップページにリダイレクトする
  // 2. Biz連携有り＆MikatanoIDからサインインした場合
  //    → Bizsolの通常ログイン画面(BLGI001)にリダイレクトする
  // 3. Biz連携待ち or Biz連携無し
  //    → Bizsolの通常ログイン画面(BLGI001)にリダイレクトする
  useEffect(() => {
    // BizsolStateの取得が完了していない場合は何もしない
    if (!called || bizsolConnectedState === null) {
      return;
    }

    if (bizsolConnectedState === BizsolConnectedState.Connected) {
      if (isLogedInFromBizsol) {
        // 1. Biz連携有り&Bizsolからサインインした場合
        const url = `https://${fqdn}/${config.BANK_CODE}${env}/oauth/idconnect_service/return`;
        Router.push(url);
      } else {
        // 2. Biz連携有り&MikatanoIDからサインインした場合
        const url = `https://${fqdn}/${config.BANK_CODE}${env}/rblgi01/I1RBLGI01-S01.do`;
        Router.push(url);
      }
    } else {
      // 3. Biz連携待ち or Biz連携無し
      const url = `https://${fqdn}/${config.BANK_CODE}${env}/rblgi01/I1RBLGI01-S01.do`;
      Router.push(url);
    }
  }, [called, bizsolConnectedState]);

  return <></>;
};
